import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import tuturi from "../asset/tuturi.png";
import DarkModeToggle from "./ThemeToggle";

const Header = ({ handleLogout }) => {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [schoolData, setSchoolData] = useState([]);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  /* ================= FETCH DATA SEKOLAH ================= */
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/datasekolah/all`)
      .then((res) => setSchoolData(res.data))
      .catch(() => {});
  }, []);

  /* ================= FETCH PROFILE USER ================= */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setRole("");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
        setRole(res.data.role);
      })
      .catch((err) => {
        // 🔥 TOKEN EXPIRED / INVALID
        if (err.response?.status === 401) {
          localStorage.removeItem("token");
          setUser(null);
          setRole("");
          navigate("/login", { replace: true });
        }
      });
  }, [navigate]);

  /* ================= CLICK OUTSIDE ================= */
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ================= LOGOUT ================= */
  const logout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <header className="sticky top-0 z-50 bg-primary dark:bg-darkBg shadow-md">
      {/* ================= TOP BAR ================= */}
      <div className="bg-secondary dark:bg-darkCard px-4 py-2 flex justify-between items-center">
        <DarkModeToggle />

        {user ? (
          <button
            onClick={logout}
            className="bg-white text-primary dark:bg-yellow-400 dark:text-black
                       px-4 py-1 rounded-full text-sm font-semibold
                       hover:scale-105 transition"
          >
            Logout
          </button>
        ) : (
          <Link
            to="/login"
            className="bg-white text-primary dark:bg-yellow-400 dark:text-black
                       px-4 py-1 rounded-full text-sm font-semibold
                       hover:scale-105 transition"
          >
            Masuk
          </Link>
        )}
      </div>

      {/* ================= MAIN NAV ================= */}
      <div className="flex items-center justify-between px-5 py-4 lg:px-10">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img src={tuturi} alt="logo" className="w-10 h-10" />
          <span className="text-white font-bold text-sm md:text-lg truncate max-w-[260px]">
            {schoolData[0]?.namasekolah || "Nama Sekolah"}
          </span>
        </div>

        {/* ================= DESKTOP MENU ================= */}
        <ul className="hidden lg:flex gap-8 text-white font-semibold">
          <Link to="/">Beranda</Link>
          <Link to="/profilsekolah">Profil Sekolah</Link>
          <Link to="/berita">Berita</Link>

          {role === "admin" && <Link to="/admin">Dashboard Admin</Link>}
          {role === "guru" && <Link to="/guru">Dashboard Guru</Link>}
          {role === "siswa" && <Link to="/siswa">Dashboard Siswa</Link>}

          {user && <Link to="/perpustakaan">Perpustakaan</Link>}
        </ul>

        {/* ================= PROFILE DESKTOP ================= */}
        {user && (
          <div
            onClick={() => navigate(`/profile/${user._id}`)}
            className="hidden lg:flex items-center gap-3 cursor-pointer"
          >
            <img
              src={
                user.image?.filename
                  ? `${process.env.REACT_APP_API_URL}api/auth/users/image/${user.image.filename}`
                  : "/avatar.png"
              }
              className="w-9 h-9 rounded-full ring-2 ring-secondary object-cover"
              alt="profile"
            />
            <span className="text-white hover:text-secondary transition">
              {user.name}
            </span>
          </div>
        )}

        {/* ================= MOBILE BUTTON ================= */}
        <button className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <FiX size={28} /> : <FiMenu size={28} />}
        </button>
      </div>

      {/* ================= MOBILE MENU ================= */}
      <div
        ref={menuRef}
        className={`lg:hidden bg-primary dark:bg-darkBg
        transition-all duration-300 overflow-hidden
        ${open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="p-5">
          {user && (
            <div
              onClick={() => {
                navigate(`/profile/${user._id}`);
                setOpen(false);
              }}
              className="flex items-center gap-4 mb-6 cursor-pointer
                         p-3 rounded-lg bg-secondary/20"
            >
              <img
                src={
                  user.image?.filename
                    ? `${process.env.REACT_APP_API_URL}api/auth/users/image/${user.image.filename}`
                    : "/avatar.png"
                }
                className="w-12 h-12 rounded-full ring-2 ring-secondary object-cover"
                alt="profile"
              />
              <div>
                <p className="text-white font-semibold">{user.name}</p>
                <p className="text-xs text-gray-300 capitalize">{role}</p>
              </div>
            </div>
          )}

          <ul className="flex flex-col gap-4 text-white font-semibold">
            <Link to="/" onClick={() => setOpen(false)}>
              Beranda
            </Link>
            <Link to="/profilsekolah" onClick={() => setOpen(false)}>
              Profil Sekolah
            </Link>
            <Link to="/berita" onClick={() => setOpen(false)}>
              Berita
            </Link>

            {role === "admin" && <Link to="/admin">Dashboard Admin</Link>}
            {role === "guru" && <Link to="/guru">Dashboard Guru</Link>}
            {role === "siswa" && <Link to="/siswa">Dashboard Siswa</Link>}

            {user && <Link to="/perpustakaan">Perpustakaan</Link>}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
